
if (!customElements.get('cart-builder-groups')) {
  class CartBuilderGroups extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected && !this.grouped) this.groupItems();
      });
    }

    disconnectedCallback() {
      this.form?.removeEventListener('formdata', this.formDataHandler);
      this.grouped = false;
    }

    groupItems() {
      const table = this.querySelector('table');
      if (!table) return;

      const { model, labels, locale, currency } = JSON.parse(this.dataset.config);
      if (!model.requirements.length) return;

      this.grouped = true;

      const money = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
      });

      const rows = Array.from(table.querySelectorAll('.cart-item'))
        .sort((a, b) =>
          Number(a.id.replace('cart-item-', '')) -
          Number(b.id.replace('cart-item-', ''))
        );

      this.form = this.closest('form');

      // Shopify's updates[] follows cart order, even when rows
      // are visually regrouped.
      this.formDataHandler = (event) => {
        event.formData.delete('updates[]');

        rows.forEach((row) => {
          const input = row.querySelector('[name="updates[]"]');

          if (input && !input.disabled) {
            event.formData.append('updates[]', input.value);
          }
        });
      };

      this.form?.addEventListener('formdata', this.formDataHandler);

      const originalBodies = Array.from(table.tBodies);

      const machines = new Set(
        model.requirements.map((rule) => String(rule.machineProductId))
      );

      const assigned = new Map();

      const groups = model.requirements.map((rule) => {
        const machineRows = rows.filter(
          (row) => row.dataset.productId === String(rule.machineProductId)
        );

        machineRows.forEach((row) => assigned.set(row, rule));

        return {
          rule,
          machineRows,
          supplyRows: []
        };
      });

      // A shared compatible supply belongs to the first
      // matching setup, once.
      groups.forEach((group) => {
        const supplies = new Set(
          group.rule.steps.flatMap((step) =>
            step.productIds.map(String)
          )
        );

        group.supplyRows = rows.filter(
          (row) =>
            !assigned.has(row) &&
            !machines.has(row.dataset.productId) &&
            supplies.has(row.dataset.productId)
        );

        group.supplyRows.forEach((row) =>
          assigned.set(row, group.rule)
        );

        const body = document.createElement('tbody');
        body.className = 'cart-builder-group';

        const headingRow = body.insertRow();
        headingRow.className = 'cart-builder-group__header';

        const cell = headingRow.insertCell();
        cell.colSpan = 4;

        const title = document.createElement('strong');
        title.className = 'cart-builder-group__title';
        title.textContent = labels.title.replace(
          '[name]',
          group.rule.builderTitle
        );

        const subtotal = document.createElement('strong');
        subtotal.className = 'cart-builder-group__subtotal';

        const groupedRows = [
          ...group.machineRows,
          ...group.supplyRows
        ];

        subtotal.textContent = money.format(
          groupedRows.reduce(
            (sum, row) =>
              sum + Number(row.dataset.finalLinePrice),
            0
          ) / 100
        );

        const contents = document.createElement('span');
        contents.className = 'cart-builder-group__contents';

        // Count the total number of selected supply packs,
        // not the number of different supply product types.
        const supplyPacks = group.supplyRows.reduce((total, row) => {
          const quantityInput = row.querySelector(
            '[name="updates[]"]'
          );

          const quantity = Number(quantityInput?.value);

          if (!Number.isSafeInteger(quantity) || quantity < 0) {
            return total;
          }

          return total + quantity;
        }, 0);

        const packLabel = supplyPacks === 1
          ? 'pack'
          : 'packs';

        contents.textContent = labels.contents.replace(
          '[count]',
          `${supplyPacks} ${packLabel}`
        );

        const edit = document.createElement('a');
        edit.className = 'cart-builder-group__edit';
        edit.href = group.rule.machineUrl;
        edit.textContent = labels.edit;

        cell.append(title, subtotal, contents, edit);

        groupedRows.forEach((row) => body.append(row));

        group.body = body;
      });

      const bodiesByRule = new Map(
        groups.map((group) => [group.rule, group.body])
      );

      const rendered = new Set();
      let regularBody;

      rows.forEach((row) => {
        const rule = assigned.get(row);

        if (rule) {
          if (!rendered.has(rule)) {
            table.append(bodiesByRule.get(rule));
            rendered.add(rule);
            regularBody = null;
          }
        } else {
          if (!regularBody) {
            regularBody = document.createElement('tbody');
            table.append(regularBody);
          }

          regularBody.append(row);
        }
      });

      originalBodies.forEach((body) => body.remove());

      this.classList.add('is-grouped');
    }
  }

  customElements.define('cart-builder-groups', CartBuilderGroups);
}
